import { useCallback } from 'react';
import { useUser } from '../../authentication';
import capitalizeFilter from '../../../filters/capitalize-filter';
import { useOrg } from '../../org';
import apiClient from '../../../services/apiClient';
import windowService from '../../../services/windowService';
import { logout } from '../../authentication';

const CATALOG_LABEL = capitalizeFilter('training');
const ORG_ICON_CLASS = 'fas fa-church fa-fw';

export type NavbarAction = {
  key: string;
  label: string;
  iconClass: string;
  active?: boolean;
  qaHook: string;
  onSelect: () => void;
};

type UseNavBarProps = {
  origin: string;
  activeTab?: string;
  hideNavbar?: boolean;
};

export default function useNavbar({ origin, activeTab }: UseNavBarProps): {
  activeTab: string;
  handleLogout: () => void;
  primaryActions: NavbarAction[];
  moreActions: NavbarAction[];
  loggedOutActions: NavbarAction[];
  orgName: string | undefined;
  orgLogoUrl: string | undefined;
} {
  const { user } = useUser();
  const { org } = useOrg();

  const hasPermission = useCallback((p: string) => user?.permissions?.includes(p) ?? false, [user?.permissions]);

  const orgName = org?.name ?? undefined;
  const helpLabel = origin === 'GRID' ? 'Help' : 'Help & Feedback';
  const whiteLabelOrgId = window.getWhiteLabelOrgId?.();
  const loggedIn = Boolean(user?.userId);
  const accountIds = loggedIn ? user?.accountIds ?? [] : [];

  const orgLogoUrl = (() => {
    if (!org?.logo) return undefined;
    if (org.logo.startsWith('http')) return org.logo;
    const base = apiClient.getConfig?.()?.baseURL || '';
    return `${base}/v1/images/org/${org.logo}`;
  })();

  const showSearch = org?.showSearch !== false;

  const showMainOrgNav =
    hasPermission('Account View') ||
    hasPermission('Roles View') ||
    hasPermission('Assign View') ||
    hasPermission('Build View') ||
    hasPermission('Groups View') ||
    hasPermission('Coach View');

  const handleLogout = () => logout();

  const showSwitchOrg = accountIds.length >= 2 && !whiteLabelOrgId;

  const showCurriculumNav =
    origin === 'GRID' && (!accountIds.length || hasPermission('Account View') || hasPermission('Assign View'));

  const primaryActions: NavbarAction[] = [];

  if (showMainOrgNav) {
    primaryActions.push({
      key: 'organization',
      label: orgName || 'Organization',
      iconClass: ORG_ICON_CLASS,
      qaHook: 'menuOrganization',
      active: activeTab === 'Organization',
      onSelect: () => windowService.redirectTo('/org'),
    });
  }

  if (hasPermission('Group Leader') && !hasPermission('Roles View')) {
    primaryActions.push({
      key: 'groups',
      label: 'Your Groups',
      iconClass: 'fas fa-users',
      qaHook: 'yourGroups',
      active: activeTab === 'People',
      onSelect: () => windowService.redirectTo('/groups'),
    });
  }

  if (hasPermission('Roles View')) {
    primaryActions.push({
      key: 'people',
      label: 'People',
      iconClass: 'fas fa-user-friends fa-fw',
      qaHook: 'menuPeople',
      active: activeTab === 'People',
      onSelect: () => windowService.redirectTo('/people'),
    });
  }

  if (showCurriculumNav) {
    primaryActions.push({
      key: 'curriculum',
      label: 'Curriculum',
      iconClass: 'fas fa-bible fa-fw',
      qaHook: 'menuCurriculum',
      active: activeTab === 'Curriculum',
      onSelect: () => windowService.redirectTo('/bible-studies'),
    });
  }

  if (showSearch) {
    primaryActions.push({
      key: 'catalog',
      label: CATALOG_LABEL,
      iconClass: 'fas fa-graduation-cap',
      qaHook: 'menuCatalog',
      active: activeTab === 'Catalog',
      onSelect: () => windowService.redirectTo('/catalog'),
    });
  }

  const moreActions: NavbarAction[] = [
    {
      key: 'profile',
      label: 'Profile',
      iconClass: 'fas fa-user fa-fw',
      qaHook: 'dropdownProfile',
      onSelect: () => windowService.redirectTo('/account-profile'),
    },
  ];

  if (showSwitchOrg) {
    moreActions.push({
      key: 'switch-org',
      label: 'Switch Church',
      iconClass: 'icon ion-arrow-swap',
      qaHook: 'dropdownswitchOrganization',
      onSelect: () => windowService.redirectTo('/org/switch'),
    });
  }

  moreActions.push(
    {
      key: 'help',
      label: helpLabel,
      iconClass: 'fas fa-question-circle fa-fw',
      qaHook: 'dropdownHelpFeedback',
      onSelect: () => windowService.redirectTo('/help'),
    },
    {
      key: 'logout',
      label: 'Log Out',
      iconClass: 'fas fa-sign-out-alt fa-fw',
      qaHook: 'dropdownSignOut',
      onSelect: handleLogout,
    }
  );

  const loggedOutActions: NavbarAction[] = [];

  if (showSearch) {
    loggedOutActions.push({
      key: 'catalog',
      label: CATALOG_LABEL,
      iconClass: 'fas fa-graduation-cap',
      qaHook: 'dropdownCatalog',
      active: activeTab === 'Catalog',
      onSelect: () => windowService.redirectTo('/catalog'),
    });
  }

  loggedOutActions.push({
    key: 'help',
    label: helpLabel,
    iconClass: 'fas fa-question-circle fa-fw',
    qaHook: 'dropdownHelpFeedback',
    onSelect: () => windowService.redirectTo('/help'),
  });

  return {
    activeTab: activeTab ?? '',
    handleLogout,
    primaryActions,
    moreActions,
    loggedOutActions,
    orgName,
    orgLogoUrl,
  };
}
